import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Clock,
  User,
  Heart,
  Home,
  Sparkles,
  Send,
  Edit,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Care Plan Generator
 * 
 * Step 4 of the Intake Workflow
 * Generates a care plan based on the completed assessment
 * Uses AI to recommend services based on ADL/IADL levels
 */

export default function CarePlanGenerator() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [carePlan, setCarePlan] = useState(null);
  
  // Editable care plan fields
  const [schedule, setSchedule] = useState([]);
  const [adlServices, setAdlServices] = useState([]);
  const [iadlServices, setIadlServices] = useState([]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [caregiverRequirements, setCaregiverRequirements] = useState('');

  useEffect(() => {
    fetchData();
  }, [requestId]);

  const fetchData = async () => {
    try {
      // Fetch service request
      const reqRes = await fetch(`${API_URL}/api/intake/service-requests/${requestId}`, {
        credentials: 'include'
      });
      if (reqRes.ok) {
        const reqData = await reqRes.json();
        setRequest(reqData);

        // Fetch assessment for this request
        const assessRes = await fetch(`${API_URL}/api/intake/assessments?service_request_id=${requestId}&limit=1`, {
          credentials: 'include'
        });
        if (assessRes.ok) {
          const assessData = await assessRes.json();
          if (assessData.items && assessData.items.length > 0) {
            setAssessment(assessData.items[0]);
            // Auto-generate care plan suggestions
            generatePlanSuggestions(assessData.items[0], reqData);
          }
        }
      } else {
        toast.error('Service request not found');
        navigate('/organization/data-entry');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generatePlanSuggestions = (assessmentData, requestData) => {
    // Generate schedule from service request
    const scheduleDays = [];
    const serviceDays = requestData.service_days || {};
    const hoursPerDay = requestData.hours_per_day || 4;
    const beginTime = requestData.begin_time || '09:00';
    const endTime = requestData.end_time || '13:00';

    Object.entries(serviceDays).forEach(([day, selected]) => {
      if (selected) {
        scheduleDays.push({
          day_of_week: day,
          start_time: beginTime,
          end_time: endTime,
          hours: hoursPerDay,
        });
      }
    });
    setSchedule(scheduleDays);

    // Generate ADL services from assessment
    const adlAssessment = assessmentData.adl_assessment || {};
    const generatedAdlServices = [];
    
    Object.entries(adlAssessment).forEach(([key, level]) => {
      if (level && level !== 'independent') {
        generatedAdlServices.push({
          key,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          level,
          included: true,
        });
      }
    });
    setAdlServices(generatedAdlServices);

    // Generate IADL services from assessment
    const iadlAssessment = assessmentData.iadl_assessment || {};
    const generatedIadlServices = [];
    
    Object.entries(iadlAssessment).forEach(([key, level]) => {
      if (level && level !== 'independent') {
        generatedIadlServices.push({
          key,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          level,
          included: true,
        });
      }
    });
    setIadlServices(generatedIadlServices);

    // Generate special instructions
    const notes = assessmentData.additional_notes || '';
    setSpecialInstructions(notes);

    // Generate caregiver requirements
    const requirements = [];
    if (adlAssessment.transferring === 'maximal' || adlAssessment.transferring === 'dependent') {
      requirements.push('Must be able to assist with transfers');
    }
    if (generatedAdlServices.length > 3) {
      requirements.push('Experience with ADL care required');
    }
    if (assessmentData.mental_assessment?.anxious || assessmentData.mental_assessment?.depressed) {
      requirements.push('Patience and compassion required');
    }
    setCaregiverRequirements(requirements.join('. '));
  };

  const toggleAdlService = (index) => {
    setAdlServices(prev => prev.map((s, i) => 
      i === index ? { ...s, included: !s.included } : s
    ));
  };

  const toggleIadlService = (index) => {
    setIadlServices(prev => prev.map((s, i) => 
      i === index ? { ...s, included: !s.included } : s
    ));
  };

  const getTotalWeeklyHours = () => {
    return schedule.reduce((sum, day) => sum + (day.hours || 0), 0);
  };

  const handleSaveCarePlan = async () => {
    if (!assessment) {
      toast.error('Assessment required before creating care plan');
      return;
    }

    setSaving(true);
    try {
      const includedAdlNeeds = adlServices.filter(s => s.included).map(s => s.key);
      const includedIadlNeeds = iadlServices.filter(s => s.included).map(s => s.key);

      const res = await fetch(`${API_URL}/api/intake/care-plans`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: request.client_id || requestId,
          assessment_id: assessment.id,
          service_request_id: requestId,
          client_name: request.client_name,
          client_address: request.client_address,
          start_date: request.start_of_care_date || new Date().toISOString().split('T')[0],
          schedule: schedule,
          care_needs_summary: `ADL needs: ${includedAdlNeeds.join(', ')}. IADL needs: ${includedIadlNeeds.join(', ')}.`,
          adl_needs: includedAdlNeeds,
          iadl_needs: includedIadlNeeds,
          special_instructions: specialInstructions,
          caregiver_requirements: caregiverRequirements,
          payment_type: request.payment_type || 'private_pay',
        })
      });

      if (res.ok) {
        const data = await res.json();
        setCarePlan(data.care_plan);
        toast.success('Care plan created successfully!');
        // Navigate to agreement step
        navigate(`/intake/agreement/${requestId}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create care plan');
      }
    } catch (err) {
      console.error('Error creating care plan:', err);
      toast.error('Failed to create care plan');
    } finally {
      setSaving(false);
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'supervision': return 'bg-yellow-100 text-yellow-700';
      case 'minimal': return 'bg-blue-100 text-blue-700';
      case 'moderate': return 'bg-orange-100 text-orange-700';
      case 'maximal': return 'bg-red-100 text-red-700';
      case 'dependent': return 'bg-red-200 text-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/organization/data-entry')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to CareHub
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#0D1B33]">Care Plan</h1>
              <p className="text-sm text-[#64748B]">
                Step 4 of 6 — {request?.client_name}
              </p>
            </div>
          </div>
        </header>

        {/* No Assessment Warning */}
        {!assessment && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900">Assessment Required</h3>
                <p className="text-amber-700 mt-1">
                  Please complete the client assessment before creating a care plan.
                </p>
                <Button
                  className="mt-4 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate(`/intake/assessment/${requestId}`)}
                >
                  Complete Assessment
                </Button>
              </div>
            </div>
          </div>
        )}

        {assessment && (
          <>
            {/* AI Suggestion Banner */}
            <div className="bg-gradient-to-r from-[#176DFF]/10 to-[#29C0FF]/10 rounded-xl border border-[#176DFF]/20 p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#176DFF] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0D1B33]">Leynix Care Plan Suggestions</h3>
                  <p className="text-sm text-[#64748B]">
                    Based on the assessment, we&apos;ve recommended services below. Review and adjust as needed.
                  </p>
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#176DFF]" />
                    <h2 className="font-semibold text-[#0D1B33]">Weekly Schedule</h2>
                  </div>
                  <span className="text-sm font-medium text-[#176DFF]">
                    {getTotalWeeklyHours()} hours/week
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {schedule.map((day, idx) => (
                    <div
                      key={idx}
                      className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E2E8F0]"
                    >
                      <div className="font-medium text-[#0D1B33] capitalize mb-1">{day.day_of_week}</div>
                      <div className="text-sm text-[#64748B]">
                        {day.start_time} - {day.end_time}
                      </div>
                      <div className="text-xs text-[#176DFF] mt-1">{day.hours}h</div>
                    </div>
                  ))}
                </div>
                {schedule.length === 0 && (
                  <p className="text-[#64748B] text-center py-4">No schedule days defined in service request</p>
                )}
              </div>
            </div>

            {/* ADL Services */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  <h2 className="font-semibold text-[#0D1B33]">ADL Services</h2>
                  <span className="text-sm text-[#64748B]">({adlServices.filter(s => s.included).length} selected)</span>
                </div>
              </div>
              <div className="p-5">
                {adlServices.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {adlServices.map((service, idx) => (
                      <label
                        key={service.key}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          service.included
                            ? 'border-[#176DFF] bg-[#176DFF]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={service.included}
                            onChange={() => toggleAdlService(idx)}
                            className="w-4 h-4 rounded text-[#176DFF]"
                          />
                          <span className="font-medium text-[#0D1B33]">{service.label}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getLevelBadgeColor(service.level)}`}>
                          {service.level}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#64748B] text-center py-4">
                    No ADL needs identified in assessment (client may be independent)
                  </p>
                )}
              </div>
            </div>

            {/* IADL Services */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <div className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-emerald-500" />
                  <h2 className="font-semibold text-[#0D1B33]">IADL Services</h2>
                  <span className="text-sm text-[#64748B]">({iadlServices.filter(s => s.included).length} selected)</span>
                </div>
              </div>
              <div className="p-5">
                {iadlServices.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {iadlServices.map((service, idx) => (
                      <label
                        key={service.key}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          service.included
                            ? 'border-emerald-400 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={service.included}
                            onChange={() => toggleIadlService(idx)}
                            className="w-4 h-4 rounded text-emerald-600"
                          />
                          <span className="font-medium text-[#0D1B33]">{service.label}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getLevelBadgeColor(service.level)}`}>
                          {service.level}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#64748B] text-center py-4">
                    No IADL needs identified in assessment (client may be independent)
                  </p>
                )}
              </div>
            </div>

            {/* Special Instructions */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <h2 className="font-semibold text-[#0D1B33]">Special Instructions</h2>
              </div>
              <div className="p-5">
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Any special instructions for caregivers..."
                  className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                  rows={4}
                />
              </div>
            </div>

            {/* Caregiver Requirements */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-[#176DFF]" />
                  <h2 className="font-semibold text-[#0D1B33]">Caregiver Requirements</h2>
                </div>
              </div>
              <div className="p-5">
                <textarea
                  value={caregiverRequirements}
                  onChange={(e) => setCaregiverRequirements(e.target.value)}
                  placeholder="Skills or certifications required for this client..."
                  className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                  rows={3}
                />
              </div>
            </div>

            {/* Summary */}
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-emerald-900">Care Plan Summary</h3>
                  <div className="mt-2 space-y-1 text-sm text-emerald-700">
                    <p><strong>Client:</strong> {request?.client_name}</p>
                    <p><strong>Weekly Hours:</strong> {getTotalWeeklyHours()}</p>
                    <p><strong>ADL Services:</strong> {adlServices.filter(s => s.included).length}</p>
                    <p><strong>IADL Services:</strong> {iadlServices.filter(s => s.included).length}</p>
                    <p><strong>Start Date:</strong> {request?.start_of_care_date || 'TBD'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => navigate(`/intake/assessment/${requestId}`)}
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit Assessment
              </Button>
              <Button
                className="bg-[#176DFF] hover:bg-[#176DFF]/90"
                onClick={handleSaveCarePlan}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Create Care Plan & Continue
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
